<template>
  <div class="full-height" ref="fullHeightRef">
    <div v-if="isInitializing" class="splash"
      style="display:flex;align-items:center;justify-content:center;min-height:100%;flex-direction:column;gap:12px;">
      <img src="@assets/images/pantalla-carga-animada.svg" alt="Cargando" />
      <div style="color:#667790;font-size:14px;">{{ $t("components.chargersetupview.cargando_datos_iniciales") }}</div>
    </div>
    <template v-else>
      <div class="logo-content-container">
        <!-- 1. Logotipo Endesa -->
        <div class="logo-container">
          <img class="logo" src="@assets/images/logo.svg" alt="Endesa" />
        </div>
      </div>
      <div class="content">
        <!-- 2. Mensajes de alerta -->
        <div class="message-container" v-if="alertMessage">
          <div class="location-icon"><img src="@assets/images/location-dot.svg" alt="Location" /></div>
          <div class="message-text">
            {{ alertMessage }}
            <a href="#" @click.prevent="showLocationHelp = true" class="help-link">{{
              $t("components.chargersetupview.ayuda") }}</a>
          </div>
        </div>

        <!-- 3. Datos del vehículo del usuario -->
        <div class="vehicle-info-container" @click="showCarPicker = true">
          <div class="vehicle-info-icon">
            <img src="@assets/images/vehicle-info.svg" alt="Car" />
          </div>

          <div class="vehicle-info">
            <div class="vehicle-info-title">{{ $t("components.chargersetupview.datos_vehiculo") }}</div>
            <div class="vehicle-description">{{ vehicleDescription }}</div>
          </div>
          <span class="edit-icon"><img src="@assets/images/pencil.svg" alt="pencil" /></span>
        </div>
        <div class="form-container">
          <!-- 4. Desplegable selección de tarifa -->
          <div class="tariff-selector-container">
            <label for="tarifa">{{ $t("components.chargersetupview.seleccione_tarifa") }}</label>
            <select id="tarifa" v-model="selectedTariff" :disabled="isLoadingTariffs" class="form-select">
              <option disabled value="">
                {{ isLoadingTariffs ? $t("components.chargersetupview.cargando_tarifas") :
                  $t("components.chargersetupview.seleccione_tarifa") }}
              </option>
              <option v-for="t in tariffs" :key="t.value" :value="t.value">{{ t.label }}</option>
            </select>
          </div>

          <!-- 5. Batería actual del vehículo -->
          <div class="battery-current-container">
            <label for="bateria-actual">{{ $t("components.chargersetupview.bateria_actual") }}</label>
            <div :class="['battery-input-group', { 'battery-error': batteryError }]">
              <input type="range" min="0" max="99" step="1" v-model.number="batteryCurrentPercent" class="form-range"
                id="bateria-actual-range" @input="validateBattery" @blur="validateBattery"
                :aria-describedby="batteryError ? 'battery-error' : undefined" style="flex: 1 1 auto; min-width: 0;" />
              <span class="battery-current-percent">{{ batteryCurrentPercent }}%</span>
            </div>
          </div>

          <!-- 6. Batería objetivo -->
          <div class="battery-target-container">
            <label for="bateria-objetivo">{{ $t("components.chargersetupview.bateria_objetivo") }}</label>
            <div :class="['battery-input-group', { 'battery-error': batteryError }]">
              <input type="range" min="1" max="100" step="1" v-model.number="batteryTargetPercent" class="form-range"
                id="bateria-objetivo-range" @input="validateBattery" @blur="validateBattery"
                :aria-describedby="batteryError ? 'battery-error' : undefined" style="flex: 1 1 auto; min-width: 0;" />
              <span class="battery-target-percent">{{ batteryTargetPercent }}%</span>
            </div>
            <!-- Error message -->
            <div v-if="batteryError" id="battery-error" class="error-message" aria-live="polite">
              <span class="error-icon">⚠️</span>
              {{ batteryErrorMessage }}
            </div>
          </div>

          <!-- Selector de idioma -->
          <div class="language-selector" hidden>
            <label>Idioma: </label>
            <select v-model="selectedLanguage" @change="changeLanguage" class="language-select">
              <option value="es-ES">Español</option>
              <option value="en-US">English</option>
              <option value="gl-ES">Galego</option>
              <option value="eu-ES">Euskera</option>
              <option value="ca-ES">Català</option>
            </select>
          </div>
        </div>
      </div>
      <!-- 7. Botón Buscar Estaciones -->
      <div class="search-button-container d-grid">
        <button class="btn btn-primary btn-lg" :disabled="!canSearch" @click="goToResults" ref="searchButton">
          {{ $t("components.chargersetupview.buscar_estaciones") }}
        </button>
        <!-- Helper text cuando hay errores -->
        <div v-if="!canSearch && batteryError" class="helper-text">
          {{ $t("components.chargersetupview.corrige_errores") }}
        </div>
      </div>


      <!-- Modal CarModelPicker -->
      <CarModelPicker v-if="showCarPicker" @close="showCarPicker = false" @select="onVehicleSelected" />

      <!-- Modal LocationHelpPicker -->
      <LocationHelpPicker v-if="showLocationHelp" @close="showLocationHelp = false" />
    </template>
  </div>

</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from 'vue-i18n';
import { useIntl } from "@services/i18n/i18nservice";
import CarModelPicker from "@/components/setup/CarModelPicker.vue";
import LocationHelpPicker from "@/components/setup/LocationHelpPicker.vue";
import { useApi } from "@api/integration"
// Inicializar API
const api = useApi();
const { t, locale } = useI18n();
const router = useRouter();
const intl = useIntl();

// Selector de idioma
const selectedLanguage = ref<string>(intl.getCurrentLocale() || 'es-ES');

async function changeLanguage() {
  // setLocale actualiza automáticamente la URL y ejecuta el callback onLocaleChanged
  await intl.setLocale(selectedLanguage.value);
}

const isInitializing = ref<boolean>(true);
const fullHeightRef = ref<HTMLElement | null>(null);

// 2. Geolocalización
const userLocation = ref<{ lat: number; lon: number } | null>(null);
const userAddress = ref<string>("");
const locationError = ref<string>("");
const alertMessage = computed(() => {
  // Mostrar el mensaje de error si existe, incluso aunque estemos usando una ubicación por defecto
  if (locationError.value) return locationError.value;
  if (!userLocation.value)
    return t("components.chargersetupview.necesario_activar_ubicacion");
  return null;
});

// Ubicación por defecto (Madrid)
const DEFAULT_LOCATION = {
  lat: 40.4169473,
  lon: -3.7035285,
  name: "Madrid, España"
};

// Función para obtener la dirección desde coordenadas (geocodificación inversa)
async function getAddressFromCoordinates(lat: number, lon: number): Promise<string> {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&addressdetails=1&accept-language=es`,
      {
        headers: {
          'User-Agent': 'Endesa-Calculadora-Echarger/1.0'
        }
      }
    );

    if (!response.ok) {
      throw new Error('Error en la geocodificación inversa');
    }

    const data = await response.json();

    if (data && data.address) {
      const address = data.address;
      const parts: string[] = [];

      // Nombre de calle
      if (address.road) {
        parts.push(address.road);
      } else if (address.street) {
        parts.push(address.street);
      }

      // Número
      if (address.house_number) {
        parts.push(address.house_number);
      }

      // Provincia
      if (address.state) {
        parts.push(address.state);
      } else if (address.province) {
        parts.push(address.province);
      }

      // Si no hay partes, usar el display_name como fallback
      if (parts.length === 0 && data.display_name) {
        return data.display_name;
      }

      return parts.join(', ');
    }

    return data.display_name || '';
  } catch (error) {
    console.warn('Error obteniendo dirección:', error);
    return '';
  }
}

// Función para obtener la ubicación del usuario
async function getUserLocation() {
  // Intentar con GPS primero
  if (!navigator.geolocation) {
    console.warn('Geolocalización GPS no disponible, usando ubicación por defecto');
    locationError.value = t("components.chargersetupview.navegador_no_soporta_geolocalizacion", { location: DEFAULT_LOCATION.name });
    userLocation.value = {
      lat: DEFAULT_LOCATION.lat,
      lon: DEFAULT_LOCATION.lon
    };

    // Obtener la dirección de la ubicación por defecto en segundo plano (no bloquea)
    getAddressFromCoordinates(
      DEFAULT_LOCATION.lat,
      DEFAULT_LOCATION.lon
    ).then(address => {
      userAddress.value = address;
    }).catch(error => {
      console.warn('Error obteniendo dirección por defecto:', error);
    });
    return;
  }

  try {
    const position = await new Promise<GeolocationPosition>((resolve, reject) => {
      let errorHandled = false;
      const timeoutDuration = 5000; // 5 segundos - reducido para fallar más rápido

      // Timeout de seguridad adicional para detectar problemas silenciosos
      const safetyTimeout = setTimeout(() => {
        if (!errorHandled) {
          console.warn('Timeout de seguridad: la geolocalización no respondió');
          errorHandled = true;
          const timeoutError = {
            code: 3, // TIMEOUT
            message: 'Tiempo de espera agotado al obtener la ubicación',
            PERMISSION_DENIED: 1,
            POSITION_UNAVAILABLE: 2,
            TIMEOUT: 3
          } as GeolocationPositionError;
          reject(timeoutError);
        }
      }, timeoutDuration);

      try {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            if (!errorHandled) {
              clearTimeout(safetyTimeout);
              errorHandled = true;
              resolve(pos);
            }
          },
          (error) => {
            if (!errorHandled) {
              clearTimeout(safetyTimeout);
              errorHandled = true;
              reject(error);
            }
          },
          {
            enableHighAccuracy: true,
            timeout: 4000, // Reducido de 10s a 4s para fallar más rápido
            maximumAge: 0
          }
        );
      } catch (policyError: any) {
        // Capturar errores de política que pueden ocurrir antes del callback
        if (!errorHandled) {
          clearTimeout(safetyTimeout);
          errorHandled = true;
          console.warn('Violación de política de permisos detectada:', policyError);
          // Crear un objeto de error similar a GeolocationPositionError
          const geolocationError = {
            code: 1, // PERMISSION_DENIED
            message: policyError.message || 'Permisos de geolocalización bloqueados',
            PERMISSION_DENIED: 1,
            POSITION_UNAVAILABLE: 2,
            TIMEOUT: 3
          } as GeolocationPositionError;
          reject(geolocationError);
        }
      }
    });

    userLocation.value = {
      lat: position.coords.latitude,
      lon: position.coords.longitude
    };
    locationError.value = "";

    // Obtener la dirección desde las coordenadas en segundo plano (no bloquea)
    getAddressFromCoordinates(
      position.coords.latitude,
      position.coords.longitude
    ).then(address => {
      userAddress.value = address;
    }).catch(error => {
      console.warn('Error obteniendo dirección:', error);
    });
  } catch (error: any) {
    console.warn('Error al obtener ubicación GPS, usando ubicación por defecto:', error);

    // Determinar el mensaje de error específico
    let errorMessage = t("components.chargersetupview.no_pudo_acceder_ubicacion", { location: DEFAULT_LOCATION.name });

    if (error?.code !== undefined) {
      switch (error.code) {
        case 1: // PERMISSION_DENIED
          errorMessage = t("components.chargersetupview.permiso_ubicacion_denegado", { location: DEFAULT_LOCATION.name });
          break;
        case 2: // POSITION_UNAVAILABLE
          errorMessage = t("components.chargersetupview.ubicacion_no_disponible", { location: DEFAULT_LOCATION.name });
          break;
        case 3: // TIMEOUT
          errorMessage = t("components.chargersetupview.tiempo_espera_agotado", { location: DEFAULT_LOCATION.name });
          break;
        default:
          errorMessage = t("components.chargersetupview.no_pudo_acceder_ubicacion", { location: DEFAULT_LOCATION.name });
      }
    } else if (error?.message?.toLowerCase().includes('permission') ||
      error?.message?.toLowerCase().includes('policy') ||
      error?.message?.toLowerCase().includes('geolocation')) {
      // Detectar violaciones de política de permisos
      errorMessage = t("components.chargersetupview.geolocalizacion_bloqueada", { location: DEFAULT_LOCATION.name });
    }

    // SIEMPRE establecer el mensaje de error cuando falle
    locationError.value = errorMessage;
    userLocation.value = {
      lat: DEFAULT_LOCATION.lat,
      lon: DEFAULT_LOCATION.lon
    };

    // Obtener la dirección de la ubicación por defecto en segundo plano (no bloquea)
    getAddressFromCoordinates(
      DEFAULT_LOCATION.lat,
      DEFAULT_LOCATION.lon
    ).then(address => {
      userAddress.value = address;
    }).catch(error => {
      console.warn('Error obteniendo dirección por defecto:', error);
    });
  }
}

// 3. Datos del vehículo
const vehicleDescription = ref<string>(t("components.chargersetupview.seleccione_vehiculo"));
const selectedVehicleId = ref<string>("");
const showCarPicker = ref<boolean>(false);
const showLocationHelp = ref<boolean>(false);

// 4. Tarifa de luz
const tariffs = ref<Array<{ label: string; value: number }>>([]);
const selectedTariff = ref<number | "">("");
const isLoadingTariffs = ref<boolean>(false);

// Variable para almacenar la tarifa guardada mientras se cargan las tarifas
const savedTariff = ref<number | null>(null);

// Función para restaurar tarifa guardada (se llama cuando las tarifas están disponibles)
function restoreTariff() {
  if (savedTariff.value != null && tariffs.value.length > 0) {
    const tariffExists = tariffs.value.some(t => t.value === savedTariff.value);
    if (tariffExists) {
      selectedTariff.value = savedTariff.value;
      savedTariff.value = null; // Limpiar después de restaurar
    } else {
      console.warn('La tarifa guardada no existe en la lista actual');
    }
  }
}

// Función para cargar tarifas desde el API
async function loadTariffs() {
  isLoadingTariffs.value = true;
  try {

    const data = await api.callStandardApi(
      "tarifaGetNombreAll",
      { showLoading: false }
    );


    // Transformar los datos del API al formato esperado
    tariffs.value = data.map((item: { Id: number; CodigoTarifa: string; NombreTarifa: string }) => ({
      label: item.NombreTarifa,
      value: item.Id
    }));


  } catch (error) {
    console.error('Error cargando tarifas:', error);
    tariffs.value = [];
  } finally {
    isLoadingTariffs.value = false;
  }
}

// Función para cargar datos guardados desde localStorage
function loadSavedData() {
  try {
    const storedData = localStorage.getItem('chargerSearchData');
    if (storedData) {
      const searchData = JSON.parse(storedData);

      // Restaurar vehículo
      if (searchData.vehicle) {
        vehicleDescription.value = searchData.vehicle;
      }
      if (searchData.IdCocheElectrico) {
        selectedVehicleId.value = searchData.IdCocheElectrico;
      }

      // Guardar tarifa para restaurarla cuando se carguen las tarifas
      if (searchData.IdTarifaNombre != null) {
        savedTariff.value = searchData.IdTarifaNombre;
        // Si ya hay tarifas cargadas, restaurarla inmediatamente
        restoreTariff();
      }

      // Restaurar baterías
      if (searchData.batteryCurrent !== undefined) {
        batteryCurrentPercent.value = searchData.batteryCurrent;
      }
      if (searchData.batteryTarget !== undefined) {
        batteryTargetPercent.value = searchData.batteryTarget;
      }

      // Restaurar ubicación (aunque se intentará obtener de nuevo)
      // NO borrar el error si ya existe uno (por ejemplo, si falló la geolocalización)
      if (searchData.location) {
        userLocation.value = searchData.location;
        // Solo limpiar el error si no había uno previamente establecido
        // (por ejemplo, si la geolocalización fue exitosa antes de llamar a loadSavedData)
        if (!locationError.value) {
          locationError.value = "";
        }
      }

      // Restaurar dirección si existe
      if (searchData.address) {
        userAddress.value = searchData.address;
      }
    }
  } catch (error) {
    console.error('Error restaurando datos guardados:', error);
  }
}

// Observar cuando las tarifas se carguen para restaurar la tarifa guardada
watch(tariffs, () => {
  if (tariffs.value.length > 0) {
    restoreTariff();
  }
}, { immediate: true });

// Cargar tarifas y obtener ubicación al montar el componente
onMounted(async () => {
  isInitializing.value = true;

  // Establecer ubicación por defecto inmediatamente para mostrar la página rápido
  userLocation.value = {
    lat: DEFAULT_LOCATION.lat,
    lon: DEFAULT_LOCATION.lon
  };

  // Cargar tarifas (necesario para mostrar la página)
  await loadTariffs();

  // Cargar datos guardados (excepto tarifa que se restaura automáticamente cuando se carguen las tarifas)
  loadSavedData();

  // Mostrar la página inmediatamente sin esperar la geolocalización
  isInitializing.value = false;

  // Obtener ubicación real en segundo plano (no bloquea la carga)
  getUserLocation().catch(error => {
    // Ya tenemos ubicación por defecto, solo loguear el error
    console.warn('Error obteniendo ubicación en segundo plano:', error);
  });
});

// 5-6. Baterías
const batteryCurrentPercent = ref<number>(50);
const batteryTargetPercent = ref<number>(80);

// Validación de batería
const batteryError = ref<boolean>(false);
const batteryErrorMessage = ref<string>("");
let validationTimeout: number | null = null;

// Referencias para focus automático
const searchButton = ref<HTMLButtonElement>();

const canSearch = computed(() => {
  return (
    vehicleDescription.value !== t("components.chargersetupview.seleccione_vehiculo") &&
    selectedTariff.value !== "" &&
    batteryCurrentPercent.value >= 0 &&
    batteryCurrentPercent.value <= 99 &&
    batteryTargetPercent.value >= 1 &&
    batteryTargetPercent.value <= 100 &&
    batteryTargetPercent.value >= batteryCurrentPercent.value &&
    !batteryError.value
  );
});

// Función de validación con debounce
function validateBattery() {
  // Limpiar timeout anterior
  if (validationTimeout) {
    clearTimeout(validationTimeout);
  }

  // Debounce de 250ms
  validationTimeout = window.setTimeout(() => {
    const current = batteryCurrentPercent.value;
    const target = batteryTargetPercent.value;

    // Normalizar valores (enteros)
    const normalizedCurrent = Math.round(current);
    const normalizedTarget = Math.round(target);

    // Actualizar valores normalizados
    if (current !== normalizedCurrent) {
      batteryCurrentPercent.value = normalizedCurrent;
    }
    if (target !== normalizedTarget) {
      batteryTargetPercent.value = normalizedTarget;
    }

    // Validar rango: batería actual 0-99, batería objetivo 1-100
    const currentInRange = normalizedCurrent >= 0 && normalizedCurrent <= 99;
    const targetInRange = normalizedTarget >= 1 && normalizedTarget <= 100;

    if (!currentInRange || !targetInRange) {
      batteryError.value = true;
      batteryErrorMessage.value = t("components.chargersetupview.valores_entre_0_100");
    } else if (normalizedTarget < normalizedCurrent) {
      batteryError.value = true;
      batteryErrorMessage.value = t("components.chargersetupview.bateria_objetivo_inferior", { target: normalizedTarget, current: normalizedCurrent });
    } else {
      batteryError.value = false;
      batteryErrorMessage.value = "";
    }
  }, 250);
}

async function goToResults() {
  // Si hay errores, hacer focus al primer campo con error
  if (batteryError.value) {
    await nextTick();
    const errorField = document.querySelector('input.error') as HTMLInputElement;
    if (errorField) {
      errorField.focus();
      // Scroll suave al campo con error
      errorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    return;
  }

  // Almacenar los datos en localStorage para que la vista de resultados pueda acceder a ellos
  // La ubicación GPS y la dirección se obtendrán en la vista de resultados
  const searchData = {
    vehicle: vehicleDescription.value,
    IdCocheElectrico: selectedVehicleId.value,
    IdTarifaNombre: typeof selectedTariff.value === 'number' ? selectedTariff.value : null,
    batteryCurrent: batteryCurrentPercent.value,
    batteryTarget: batteryTargetPercent.value,
    BateriaInicial: batteryCurrentPercent.value,
    BateriaFinal: batteryTargetPercent.value,
    location: userLocation.value, // Usar la ubicación actual (puede ser la del inicio o por defecto)
    address: userAddress.value // Usar la dirección actual si existe
  };

  localStorage.setItem('chargerSearchData', JSON.stringify(searchData));

  const intl = useIntl();
  await router.push(intl.i18Route({ name: "charger-results" }));
}

function onVehicleSelected(brand: string, model: string, idCocheElectrico: string) {
  vehicleDescription.value = `${brand} ${model}`;
  selectedVehicleId.value = idCocheElectrico;
}
</script>

<style scoped lang="scss">
@import "@/styles/_vars.scss";
@import "@/styles/figma.scss";
@import "@/styles/_themevars.scss";

.full-height {
  background: var(--background-default-default, #FFF);
  height: 100dvh;
  display: grid;
  grid-template-rows: auto 1fr auto;
  width: 100%;

  .splash {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.9);
    /* Semi-transparent overlay */
    z-index: 1000;
    /* Ensure it's on top */
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100%;
    flex-direction: column;
    gap: 12px;
  }

  .logo-content-container {
    display: flex;
    justify-content: center;

    .logo-container {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: var(--spacing-sm, 12px);
      position: relative;
      padding: var(--spacing-md, 16px);
      flex: 1;

      .logo {
        width: 125px;
        height: auto;
        margin-top: var(--spacing-3xs);
      }

      .title-bar-button {
        background: transparent;
        border: 0;
        font-size: 24px;
        padding: var(--spacing-xs, 8px) var(--spacing-md, 16px);
        width: auto;
        display: flex;
        position: absolute;
        right: 0;
        top: var(--spacing-sm);

        i {
          color: var(--text-default-caption, #667790);
        }
      }
    }
  }

  .content {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 24px;
    align-self: stretch;
    overflow: auto;
    padding: var(--spacing-lg, 24px)var(--spacing-md, 16px);

    .message-container {
      display: flex;
      padding: var(--card-vertical-padding, 16px) var(--card-horizontal-padding, 16px);
      align-items: center;
      gap: 10px;
      align-self: stretch;
      border-radius: var(--card-border-radius, 16px);
      background: var(--background-normal-hover, #F0F0F0);

      .location-icon {
        width: 48px;
        height: 48px;
      }

      .message-text {
        color: var(--text-default-caption, #667790);
        font-size: var(--font-size-body-sm, 14px);
        font-weight: 400;
        line-height: var(--line-height-body-sm, 18px);

        .help-link {
          color: var(--text-primary-default, #d3135a);
          text-decoration: underline;
          cursor: pointer;
          margin-left: 4px;

          &:hover {
            color: var(--text-primary-hover, #960d40);
          }
        }
      }
    }

    .vehicle-info-container {
      display: flex;
      padding: var(--card-vertical-padding, 16px) var(--card-horizontal-padding, 16px);
      align-items: start;
      gap: 16px;
      border-radius: var(--card-border-radius, 16px);
      border: 1px solid var(--border-default-default, #C2CDDD);
      background: var(--background-default-default, #FFF);

      .vehicle-info-icon img {
        width: 48px;
        height: 48px;
      }

      .vehicle-info {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-xs);
        flex: 1;

        .vehicle-info-title {
          color: var(--text-default-caption, #667790);
          font-size: var(--font-size-body-sm, 14px);
          font-style: normal;
          font-weight: 900;
          line-height: var(--line-height-body-sm, 18px);
        }

        .vehicle-description {
          color: var(--text-default-default, #000);
          font-size: var(--font-size-heading-h4, 16px);
          font-style: normal;
          font-weight: 700;
          line-height: var(--line-height-heading-h4, 18px);
        }
      }
    }

    .form-container {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-md);


      .tariff-selector-container,
      .battery-current-container,
      .battery-target-container {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-xs);
      }

      .language-selector {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        margin-top: var(--spacing-xs);

        label {
          font-size: 14px;
          color: var(--text-default-default, #000);
        }

        .language-select {
          padding: 4px 8px;
          border: 1px solid var(--border-default-default, #C2CDDD);
          border-radius: 4px;
          font-size: 14px;
          cursor: pointer;
          background: var(--background-default-default, #FFF);
        }
      }
    }
  }

  .search-button-container {
    padding: var(--spacing-md, 16px);
  }


  input.error {
    border-color: var(--border-error-default, #DC2626);
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
  }

  input.error:focus {
    border-color: var(--border-error-default, #DC2626);
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.2);
  }

  .error-message {
    display: flex;
    align-items: center;
    gap: 6px;
    color: var(--text-error-default, #DC2626);
    font-size: 14px;
    margin-top: 6px;
    font-weight: 500;
  }

  .error-icon {
    font-size: 16px;
  }

  .helper-text {
    color: #6b7280;
    font-size: 14px;
    margin-top: 8px;
    text-align: center;
    font-style: italic;
  }
}

.battery-input-group {
  border-radius: var(--card-border-radius, 16px);
  border: 1px solid var(--border-default-default, #C2CDDD);
  background: var(--background-default-default, #FFF);
  display: flex;
  padding: var(--card-vertical-padding, 16px) var(--card-horizontal-padding, 16px);
  align-items: center;
  gap: 8px;

  &.battery-error {
    border-color: var(--border-error-default);

  }


  .battery-current-percent,
  .battery-target-percent {
    color: var(--text-default-caption, #667790);
    font-size: var(--font-size-heading-h3, 20px);
    font-weight: 700;
  }
}
</style>
